import React, { createContext, useContext, useState, useEffect } from "react";

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [token, setToken] = useState("");
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cell, setCell] = useState(null);

  // Função para obter a chave de armazenamento específica do usuário e empresa
  const getUserCellKey = (userEmail, companyName) => {
    // Inclui o nome da empresa para evitar conflitos entre empresas
    const sanitizedCompany = companyName ? companyName.replace(/[^a-zA-Z0-9]/g, '_') : 'default';
    return `ikaro_cell_${userEmail}_${sanitizedCompany}`;
  };

  // Função para validar se uma célula existe na lista de células do usuário
  const validateCellInUserCells = (cellToValidate, userCells) => {
    if (!cellToValidate || !userCells || !Array.isArray(userCells)) {
      return null;
    }
    
    // Verifica se a célula existe na lista
    // userCells pode ser tanto da API (com id/name) quanto já mapeadas (com value/title)
    const foundCell = userCells.find((userCell) => {
      const cellId = userCell.id || userCell.value;
      const cellName = userCell.name || userCell.title;
      
      return (
        cellId === cellToValidate.value ||
        cellName === cellToValidate.title
      );
    });
    
    return foundCell ? cellToValidate : null;
  };

  // Função auxiliar para salvar célula no localStorage
  const saveCellToStorage = React.useCallback((userEmail, companyName, cellData) => {
    if (userEmail && companyName && cellData) {
      const userCellKey = getUserCellKey(userEmail, companyName);
      localStorage.setItem(userCellKey, JSON.stringify(cellData));
    }
  }, []);

  // Função para carregar a célula específica do usuário e empresa
  const loadUserCell = React.useCallback((userEmail, userCells, companyName, shouldSaveAutoSelection = true) => {
    if (!userEmail || !userCells || !Array.isArray(userCells)) {
      if (userCells && userCells.length > 0) {
        const fallbackCell = {
          title: userCells[0].name || userCells[0].title,
          value: userCells[0].id || userCells[0].value
        };
        
        // Salva a seleção automática se solicitado
        if (shouldSaveAutoSelection) {
          saveCellToStorage(userEmail, companyName, fallbackCell);
        }
        
        return fallbackCell;
      }
      return null;
    }

    const userCellKey = getUserCellKey(userEmail, companyName);
    const storedCell = localStorage.getItem(userCellKey);
    
    if (storedCell) {
      try {
        const parsedCell = JSON.parse(storedCell);
        const validatedCell = validateCellInUserCells(parsedCell, userCells);
        
        if (validatedCell) {
          return validatedCell;
        } else {
          // Remove a célula inválida do localStorage
          localStorage.removeItem(userCellKey);
        }
      } catch (error) {
        console.error("Erro ao carregar célula do usuário:", error);
        localStorage.removeItem(userCellKey);
      }
    }
    
    // Retorna a primeira célula disponível como fallback
    if (userCells && userCells.length > 0) {
      const fallbackCell = {
        title: userCells[0].name || userCells[0].title,
        value: userCells[0].id || userCells[0].value
      };
      
      // Salva a seleção automática se solicitado
      if (shouldSaveAutoSelection) {
        saveCellToStorage(userEmail, companyName, fallbackCell);
      }
      
      return fallbackCell;
    }
    return null;
  }, [saveCellToStorage]);

  useEffect(() => {
    // Carregar o token do localStorage quando o componente for montado
    const storedToken = localStorage.getItem("ikaro_auth_token");
    const storedUser = localStorage.getItem("ikaro_user_data");

    if (storedToken) {
      setToken(storedToken);
    }

    if (storedUser) {
      try {
        const userData = JSON.parse(storedUser);
        setUser(userData);
        
        // Carregar célula específica do usuário
        if (userData.email && userData.cells) {
          const userCell = loadUserCell(userData.email, userData.cells, userData.company_name);
          setCell(userCell);
        }
      } catch (error) {
        console.error("Erro ao carregar dados do usuário:", error);
        localStorage.removeItem("ikaro_user_data");
      }
    }

    // Limpar células antigas do sistema antigo (sem especificação de usuário)
    const oldCell = localStorage.getItem("ikaro_cell");
    if (oldCell) {
      localStorage.removeItem("ikaro_cell");
    }

    setLoading(false);
  }, [loadUserCell]);

  // Função para buscar células atualizadas da API
  const fetchUserCells = React.useCallback(async () => {
    if (!token) {
      console.warn("Token não disponível para buscar células");
      return null;
    }

    try {
      const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
      const response = await fetch(`${API_BASE_URL}/cells/`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const result = await response.json();
        return result.data || [];
      } else {
        const errorData = await response.json();
        console.error("Erro ao buscar células atualizadas:", errorData);
        return null;
      }
    } catch (error) {
      console.error("Erro ao buscar células atualizadas:", error);
      return null;
    }
  }, [token]);

  const saveAuthData = (tokenData, userData = null) => {
    // Limpar completamente os dados do usuário anterior antes de salvar novos dados
    setCell(null);
    setUser(null);
    
    localStorage.setItem("ikaro_auth_token", tokenData);
    setToken(tokenData);

    if (userData) {
      localStorage.setItem("ikaro_user_data", JSON.stringify(userData));
      setUser(userData);
      
      // Carregar célula específica do usuário após salvar os dados
      if (userData.email && userData.cells) {
        const userCell = loadUserCell(userData.email, userData.cells, userData.company_name);
        setCell(userCell);
        
        // Limpar células antigas de outros usuários
        cleanupOtherUsersCells();
        
        // Buscar células atualizadas da API após login para garantir dados frescos
        // Fazer isso de forma assíncrona para não bloquear o login
        setTimeout(async () => {
          try {
            console.log("Buscando células atualizadas da API para usuário:", userData.email, "empresa:", userData.company_name);
            
            const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
            const response = await fetch(`${API_BASE_URL}/cells/`, {
              method: "GET",
              headers: {
                Authorization: `Bearer ${tokenData}`, // Usar tokenData diretamente
              },
            });

            if (response.ok) {
              const result = await response.json();
              const freshCells = result.data || [];
              
              console.log("Células atualizadas recebidas:", freshCells.length, "células para", userData.email);
              
              if (freshCells.length > 0) {
                // Atualizar userData com células frescas
                const updatedUserData = { ...userData, cells: freshCells };
                localStorage.setItem("ikaro_user_data", JSON.stringify(updatedUserData));
                setUser(updatedUserData);
                
                // Revalidar a célula selecionada com dados frescos
                const revalidatedCell = loadUserCell(userData.email, freshCells, userData.company_name, false);
                if (revalidatedCell) {
                  setCell(revalidatedCell);
                  console.log("Célula revalidada e selecionada:", revalidatedCell.title);
                }
              }
            } else {
              const errorData = await response.json();
              console.error("Erro ao buscar células atualizadas após login:", errorData);
            }
          } catch (error) {
            console.error("Erro ao buscar células atualizadas após login:", error);
          }
        }, 100); // Pequeno delay para não bloquear o login
      }
    }
  };

  const clearAuth = () => {
    localStorage.removeItem("ikaro_auth_token");
    localStorage.removeItem("ikaro_user_data");
    
    // Limpar completamente o estado quando faz logout
    setToken("");
    setUser(null);
    setCell(null);
  };

  const isAuthenticated = () => {
    return !!token;
  };

  const changeCell = (newCell) => {
    if (!user || !user.email) {
      console.warn("Não é possível salvar célula sem usuário autenticado");
      return;
    }

    const userCellKey = getUserCellKey(user.email, user.company_name);
    const currentStoredCell = localStorage.getItem(userCellKey);
    
    // Verifica se a célula é diferente da atual
    if (currentStoredCell) {
      try {
        const parsedCurrentCell = JSON.parse(currentStoredCell);
        if (JSON.stringify(parsedCurrentCell) === JSON.stringify(newCell)) {
          return; // Não altera se for a mesma célula
        }
      } catch (error) {
        console.error("Erro ao comparar células:", error);
      }
    }
    
    setCell(newCell);
    
    if (newCell) {
      localStorage.setItem(userCellKey, JSON.stringify(newCell));
    } else {
      localStorage.removeItem(userCellKey);
    }
  };

  const clearCell = () => {
    if (user && user.email) {
      const userCellKey = getUserCellKey(user.email, user.company_name);
      localStorage.removeItem(userCellKey);
    }
    setCell(null);
  };

  // Função para validar e ajustar a célula atual baseada na lista de células do usuário
  const validateAndAdjustCell = (userCells) => {
    if (!user || !user.email || !userCells || !Array.isArray(userCells)) {
      return;
    }

    const validatedCell = validateCellInUserCells(cell, userCells);
    
    if (!validatedCell && userCells.length > 0) {
      // Se a célula atual não é válida, seleciona a primeira disponível
      const fallbackCell = {
        title: userCells[0].name || userCells[0].title,
        value: userCells[0].id || userCells[0].value
      };
      
      // Usar changeCell para garantir que seja salva no localStorage
      changeCell(fallbackCell);
    } else if (!validatedCell) {
      // Se não há células disponíveis, limpa a célula atual
      setCell(null);
      
      // Remove do localStorage também
      if (user && user.email) {
        const userCellKey = getUserCellKey(user.email, user.company_name);
        localStorage.removeItem(userCellKey);
      }
    }
  };

  // Função para limpar células de outros usuários (manutenção do localStorage)
  const cleanupOtherUsersCells = () => {
    if (!user || !user.email) return;
    
    const currentUserCellKey = getUserCellKey(user.email, user.company_name);
    const allKeys = Object.keys(localStorage);
    
    // Remove células de outros usuários se o localStorage estiver muito cheio
    const cellKeys = allKeys.filter(key => key.startsWith('ikaro_cell_') && key !== currentUserCellKey);
    
    // Mantém apenas as 10 células mais recentes de outros usuários/empresas
    if (cellKeys.length > 10) {
      cellKeys.slice(10).forEach(key => {
        localStorage.removeItem(key);
      });
    }
  };

  // Função para forçar limpeza de estados de células em componentes
  const clearCellStates = () => {
    // Esta função pode ser chamada por componentes para garantir
    // que não há dados de células de usuários anteriores
    setCell(null);
  };

  const value = {
    token,
    user,
    saveAuthData,
    clearAuth,
    isAuthenticated,
    loading,
    // Manter compatibilidade com o código antigo
    apiKey: token,
    cell,
    saveApiKey: saveAuthData,
    clearApiKey: clearAuth,
    changeCell,
    clearCell,
    validateAndAdjustCell, // Nova função para validação externa
    loadUserCell, // Expor função para uso em componentes
    cleanupOtherUsersCells, // Função para limpeza do localStorage
    saveCellToStorage, // Função auxiliar para salvar células
    clearCellStates, // Nova função para limpeza de estados
    fetchUserCells, // Nova função para buscar células da API
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
